// FitMap Passport — a reusable personal measurement profile. Deliberately
// NOT part of domain.ts (which mirrors the D1 schema): no user-account
// system exists in this app, so the Passport lives entirely client-side
// (see src/dom/passportStorage.ts), not on the server. "Saved securely"
// here means "never leaves the device unless the user explicitly exports
// it" — there is no sync, and no account to attach it to.

import type { Audience, MeasurementKey } from '@/types/domain';

export type PreferredUnit = 'cm' | 'in';
export type PreferredFit = 'close' | 'standard' | 'relaxed' | 'oversized';
export type RisePreference = 'low-rise' | 'mid-rise' | 'high-rise';

export const PREFERRED_FIT_LABEL: Record<PreferredFit, string> = {
  close: 'Close fit',
  standard: 'Standard fit',
  relaxed: 'Relaxed fit',
  oversized: 'Oversized fit',
};

export const RISE_PREFERENCE_LABEL: Record<RisePreference, string> = {
  'low-rise': 'Low rise',
  'mid-rise': 'Mid rise',
  'high-rise': 'High rise',
};

export interface KnownBrandSize {
  id: string;
  brandName: string;
  category: 'tops' | 'bottoms';
  size: string;
}

/** Every measurement field is stored internally in centimetres, regardless of preferredUnit (which only affects display/input). */
export interface FitPassportMeasurements extends Partial<Record<MeasurementKey, number>> {
  height?: number;
  bodyLength?: number;
}

export interface FitPassport {
  /** Optional, and only ever shown back to you — e.g. as initials on the Profile chip. Never inferred or defaulted to a placeholder name. */
  displayName?: string;
  preferredUnit: PreferredUnit;
  audience: Audience;
  measurements: FitPassportMeasurements;
  risePreference: RisePreference | null;
  preferredFit: PreferredFit | null;
  knownBrandSizes: KnownBrandSize[];
  updatedAt: string; // ISO timestamp
}

export const CORE_MEASUREMENT_FIELDS: Array<{ key: keyof FitPassportMeasurements; label: string }> = [
  { key: 'height', label: 'Height' },
  { key: 'chest', label: 'Chest / bust' },
  { key: 'waist', label: 'Waist' },
  { key: 'hip', label: 'Hips / seat' },
  { key: 'shoulder', label: 'Shoulder width' },
  { key: 'inseam', label: 'Inseam' },
];

export const OPTIONAL_MEASUREMENT_FIELDS: Array<{ key: keyof FitPassportMeasurements; label: string }> = [
  { key: 'thigh', label: 'Thigh' },
  { key: 'sleeve', label: 'Sleeve length' },
  { key: 'bodyLength', label: 'Body length' },
];

export function emptyPassport(audience: Audience = 'women'): FitPassport {
  return {
    preferredUnit: 'cm',
    audience,
    measurements: {},
    risePreference: null,
    preferredFit: null,
    knownBrandSizes: [],
    updatedAt: new Date().toISOString(),
  };
}
